import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Loader2, RefreshCw, Settings2, Edit2 } from 'lucide-react';
import { formConfigsApi, signupFormsApi, emailTemplatesApi, FormConfigRecord, SignupFormRecord, EmailTemplateRecord, extractLookupId, extractLookupDisplay } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function ConfigurationsList() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [configs, setConfigs] = useState<FormConfigRecord[]>([]);
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  /** Resolve a lookup field to a human-readable name */
  const getFormName = (lookupVal: any): string => {
    // First try display_value from the lookup
    const display = extractLookupDisplay(lookupVal);
    // If display is "0" or empty, show "<not assigned>"
    if (!display || display === '0') return '<not assigned>';
    // If display is just a numeric ID, resolve from forms list
    const id = extractLookupId(lookupVal);
    if (id && forms.length > 0) {
      const form = forms.find(f => f.ID === id);
      if (form) return form.Form_Name || id;
    }
    // If display looks like a name (not just digits), use it
    if (display && !/^\d+$/.test(display)) return display;
    return display || '<not assigned>';
  };
  const getTemplateName = (lookupVal: any): string => {
    const display = extractLookupDisplay(lookupVal);
    const id = extractLookupId(lookupVal);
    if (id && templates.length > 0) {
      const template = templates.find(t => t.ID === id);
      if (template) return template.Email_Subject || id;
    }
    if (display && !/^\d+$/.test(display)) return display;
    return display || '—';
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsData, formsData, templatesData] = await Promise.all([formConfigsApi.list(), signupFormsApi.list(), emailTemplatesApi.list()]);
      setConfigs(configsData);
      setForms(formsData);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await formConfigsApi.delete(id);
      setConfigs(prev => prev.filter(c => c.ID !== id));
      setDeleteConfirm(null);
      toast.success('Configuration deleted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete configuration', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  // Filter by team and region
  const filteredConfigs = configs.filter(c => {
    if (!c.Team || c.Team !== currentWorkspace.name) return false;
    if (currentSubRegion) {
      return !c.Region || c.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-3406">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3407">
        <div data-id="element-3408">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3409">
            Form Configurations
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3410">
            Link signup forms to email templates.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3411">
          <button onClick={fetchData} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-3412">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3413" />
          </button>
          <Link to="/configuration/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-3414">
            <Plus className="h-4 w-4" data-id="element-3415" />
            <span data-id="element-3416">New Configuration</span>
          </Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3417">
        {loading && configs.length === 0 ? <div className="p-12 text-center" data-id="element-3418">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3419" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3420">
              Loading configurations...
            </p>
          </div> : configs.length === 0 ? <div className="p-12 text-center" data-id="element-3421">
            <Settings2 className="mx-auto h-12 w-12 text-gray-400" data-id="element-3422" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3423">
              No configurations found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3424">
              Link a form to an email template to get started.
            </p>
            <Link to="/configuration/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3425">
              <Plus className="h-4 w-4" data-id="element-3426" />
              Create Configuration
            </Link>
          </div> : <div className="overflow-x-auto" data-id="element-3427">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3428">
              <thead className="bg-gray-50" data-id="element-3429">
                <tr data-id="element-3430">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3431">
                    Sign Up Form
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3432">
                    Email Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3433">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3434">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3435">
                {filteredConfigs.map(config => <tr key={config.ID} className="hover:bg-gray-50" data-id="element-3436">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3437">
                      {getFormName(config.Sign_Up_Form)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3438">
                      {getTemplateName(config.Email_Template)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3439">
                      {config.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3440">
                      <div className="flex items-center justify-end gap-2" data-id="element-3441">
                        <Link to={`/configuration/${config.ID}`} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-3442">
                          <Edit2 className="h-4 w-4" data-id="element-3443" />
                        </Link>
                        <button onClick={() => setDeleteConfirm(config.ID)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-3444">
                          <Trash2 className="h-4 w-4" data-id="element-3445" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3446">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3447">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-3448" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3449">
              <div className="flex items-center gap-4" data-id="element-3450">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3451">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-3452" />
                </div>
                <div data-id="element-3453">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3454">
                    Delete Configuration
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-3455">
                    Are you sure? This will remove the link between the form and
                    template.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-3456">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3457">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3458">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3459" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}